import { VehicleImageWithMeta } from '@/catalog/types';
import { createImageUrl } from '@/catalog/utils/pdp/create-image-url';
import Spacer from '@/common/components/spacer';
import Gallery from '@/icons/gallery';
import { Button } from '@/shadcn-ui/button';
import { motion } from 'motion/react';
import { ReactNode } from 'react';
import HeroMissingImage from './hero-missing-image';
import ImagePreviewHeroGradient from './image-preview-hero-gradient';
import { findImageBySequence } from '@/catalog/utils/pdp/find-image-by-sequence';
import { findImagesByCategory } from '@/catalog/utils/pdp/find-images-by-category';

interface ImageCarouselGalleryProps {
  interiorImages: VehicleImageWithMeta[];
  exteriorImages: VehicleImageWithMeta[];
  heroImage?: VehicleImageWithMeta;
  cdnUrl: string;
  interstitialContent?: ReactNode;
  /** Image passed to preview, otherwise show default if image undefined */
  onClickDelegate: (image?: string) => void;
}

export default function ImagePreviewShort({
  interiorImages,
  exteriorImages,
  heroImage,
  cdnUrl,
  interstitialContent,
  onClickDelegate,
}: ImageCarouselGalleryProps) {
  const heroImageUrl = heroImage ? createImageUrl(cdnUrl, heroImage) : undefined;
  const exteriorSide = findImageBySequence(
    exteriorImages,
    2, // Get the third image in the exterior images, which should typically be side view
    cdnUrl
  );

  // Interior shots are a bit irregular, so we show the first 2 provided if they exist
  const interiorImagesUrls = findImagesByCategory(interiorImages, 'Interior', {
    limit: 2,
    cdnUrl,
  });

  return (
    <>
      <div className="relative aspect-[4/3] w-full">
        {heroImageUrl ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate()}
            className="relative h-fit overflow-hidden"
          >
            <motion.img
              alt="Vehicle exterior front"
              src={heroImageUrl}
              fetchPriority="high"
              loading="eager"
              className="z-0 h-full w-full object-cover"
              initial={{ scale: 1.5 }}
              whileTap={{ scale: 1.485 }}
              transition={{ duration: 0.1 }}
            />
            <ImagePreviewHeroGradient />
          </Button>
        ) : (
          <HeroMissingImage />
        )}
      </div>
      {interstitialContent}
      <Spacer variant="height24" />
      <div className="flex h-full flex-row justify-start gap-[12px] px-[24px] md:px-0">
        {exteriorSide ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(exteriorSide)}
            className="h-full w-1/4"
          >
            <motion.img
              src={exteriorSide}
              alt="Vehicle exterior side"
              className="aspect-[4/3] w-full rounded-[16px] object-cover shadow-shadow10 hover:shadow-shadow20"
              fetchPriority="high"
              loading="eager"
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            />
          </Button>
        ) : null}

        {interiorImagesUrls[0] ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(interiorImagesUrls[0])}
            className="h-full w-1/4"
          >
            <motion.img
              src={interiorImagesUrls[0]}
              alt="Vehicle interior first"
              className="aspect-[4/3] w-full rounded-[16px] object-cover shadow-shadow10 hover:shadow-shadow20"
              fetchPriority="high"
              loading="eager"
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            />
          </Button>
        ) : null}

        {interiorImagesUrls[1] ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(interiorImagesUrls[1])}
            className="h-full w-1/4"
          >
            <motion.img
              src={interiorImagesUrls[1]}
              alt="Vehicle interior second"
              className="aspect-[4/3] w-full rounded-[16px] object-cover shadow-shadow10 hover:shadow-shadow20"
              fetchPriority="high"
              loading="eager"
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            />
          </Button>
        ) : null}

        {interiorImages?.length > 0 || exteriorImages.length > 0 ? (
          <Button
            variant="clear"
            className="group flex aspect-[4/3] h-full w-1/4 flex-row items-center justify-center gap-[4px] rounded-[16px] border-[1px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 p-[8px] transition-all"
            onClick={() => onClickDelegate()}
          >
            <Gallery className="h-[24px] w-[27px] flex-shrink-0 group-hover:text-flexcar-blue40" />
            <p className="group-hover button1 hidden text-flexcar-neutral10 group-hover:text-flexcar-blue40 md:block">
              View all
            </p>
          </Button>
        ) : null}
      </div>
    </>
  );
}
